/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router"
import { useHistory } from "react-router-dom"
import { Button, Empty, Table } from "@duxton-web/ui"

import { ReturnRefundStatusTag } from "ecommerce/components/ReturnRefundStatusTag"
import { ReturnRequestDrawer } from "ecommerce/components/ReturnRequestDrawer"
import {
  RETURN_REFUND_TEXT,
  RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS,
} from "ecommerce/constants/returnRefund"
import { useReturnRequestDetail } from "ecommerce/hooks/useReturnRequestDetail"
import { useListReturnRefundWorklistQuery } from "ecommerce/queries"
import { openTimelineDestination } from "ecommerce/utils/returnRefundHelpers"
import { EventCategory } from "constants/tracking"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import { BusinessType } from "types/models/menu"

import useFeedbackStorelist from "../FeedbackFilters/hooks/useFeedbackStoreList"
import {
  getDefaultEndTimeBrowser,
  getDefaultFromTimeBrowser,
} from "../Incidents/utils"
import { ReturnRefundListFilter } from "./ReturnRefundListFilter"
import {
  ReturnRefundRequestRow,
  ReturnRefundRowItem,
  toEpochMs,
  toReturnRefundRequestRows,
} from "./utils"
import styles from "./returnRefundStyles.modules.css"

/**
 * One line per claimed item inside an item-level cell. The item columns (name, quantity,
 * amount, status) each stack their own lines, so the shared class pins a common line box
 * and the lines stay aligned across columns as long as none of them wraps.
 */
const stackedItemCell = (
  row: ReturnRefundRequestRow,
  renderItem: (item: ReturnRefundRowItem) => React.ReactNode,
) =>
  row.items.map(item => (
    <div key={item.key} className={styles.itemLine}>
      {renderItem(item)}
    </div>
  ))

/*
 * Module scope is only safe while the titles are static placeholders. When `RETURN_REFUND_TEXT`
 * is swapped for PhraseApp keys this has to move inside the component so it can read the
 * translations hook.
 */
const columns = [
  {
    title: RETURN_REFUND_TEXT.columnDate,
    dataIndex: "date",
    key: "date",
  },
  {
    title: RETURN_REFUND_TEXT.columnShortOrderNumber,
    dataIndex: "shortOrderNumber",
    key: "shortOrderNumber",
  },
  {
    title: RETURN_REFUND_TEXT.columnItemName,
    key: "itemName",
    render: (_: unknown, row: ReturnRefundRequestRow) =>
      stackedItemCell(row, item => item.itemName),
  },
  {
    title: RETURN_REFUND_TEXT.columnQuantity,
    key: "quantity",
    render: (_: unknown, row: ReturnRefundRequestRow) =>
      stackedItemCell(row, item => item.quantity),
  },
  {
    title: RETURN_REFUND_TEXT.columnAmount,
    key: "amount",
    render: (_: unknown, row: ReturnRefundRequestRow) =>
      stackedItemCell(row, item => item.amount),
  },
  {
    title: RETURN_REFUND_TEXT.columnStatus,
    key: "status",
    // Per item line, since siblings in one request can disagree — one refunded, one pending.
    // An item without status still emits its (empty) line so the column's rhythm holds.
    render: (_: unknown, row: ReturnRefundRequestRow) =>
      stackedItemCell(row, item =>
        item.statusContent ? (
          <ReturnRefundStatusTag
            label={item.statusContent}
            actionRequired={item.highlightStatus}
          />
        ) : null,
      ),
  },
]

/**
 * Return/Refund worklist for the Feedback page: one row per request, with the claimed items
 * stacked as lines inside the item-level cells.
 *
 * Distinct from the Orders tab's request-level list, whose columns are request-level only —
 * here the items stay individually visible with their own quantity, amount and status.
 * Filter state lives in the URL query params, matching the Incidents tab so a filtered view
 * stays shareable.
 */
export const ReturnRefundList: FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { isSingleStoreUser, storeID } = useSingleStoreUser()

  const queryParams = new URLSearchParams(location.search)

  const merchantID =
    queryParams.get("merchantID") || (isSingleStoreUser ? storeID ?? "" : "")

  const statusStr = queryParams.get("status") || ""
  const selectedStatus = useMemo(
    () => (statusStr ? statusStr.split(",") : []),
    [statusStr],
  )

  const fromTime = queryParams.get("fromTime") || getDefaultFromTimeBrowser()
  const endTime = queryParams.get("endTime") || getDefaultEndTimeBrowser()

  /*
   * The event category is not just tracking — `handleFilter` only applies its
   * `city`/`deliverOption` branch when the category is `feedbackHomePage`, and that branch
   * stringifies an absent city into `["undefined"]`, which matches no store. So this must
   * stay off `feedbackHomePage` (the hook's default); the business-type scope below is
   * honoured either way.
   *
   * Scoped to `BusinessType.Shop`, which covers mart and eCommerce. Returns only exist for
   * those, so an unscoped picker would list the merchant's food stores too and — via the
   * auto-select below — open the tab on one, firing a worklist request for a store that can
   * never have returns. A merchant with both is the common case, not the edge case. The
   * scope lives in the hook rather than in each call so no later search can drop it.
   */
  const {
    handleFilter,
    filteredList,
    isFetchingOutletsSearch,
  } = useFeedbackStorelist(EventCategory.returnRefundList, BusinessType.Shop)

  const handleQueryChange = useCallback(
    (params: Partial<Record<string, string | string[]>>) => {
      const newParams = new URLSearchParams(location.search)

      Object.entries(params).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          newParams.delete(key)
        } else if (Array.isArray(value)) {
          newParams.set(key, value.join(","))
        } else {
          newParams.set(key, value)
        }
      })

      history.replace({
        search: newParams.toString() ? `?${newParams.toString()}` : "",
      })
    },
    [history, location.search],
  )

  useEffect(() => {
    if (!filteredList?.length) {
      handleFilter({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Land on a store rather than an empty table when the tab is opened without one.
  useEffect(() => {
    if (!merchantID && filteredList?.length) {
      const initialMerchantID = filteredList[0]?.merchantID

      if (initialMerchantID) {
        handleQueryChange({ merchantID: initialMerchantID })
      }
    }
  }, [filteredList, merchantID, handleQueryChange])

  /*
   * The status filter comes from the URL, so it is untrusted input — the Select's options
   * are not a constraint on what can arrive here. Intersecting with the backend's own
   * allowlist means a crafted or stale link degrades to a narrower filter rather than
   * failing the whole request: the worklist validates `statusFilter` server-side and
   * rejects the entire call with invalid-argument on any unknown value, which would blank
   * the table rather than ignore the bad param.
   *
   * This also drops `all`, the Select's pseudo-option — it is not a backend value, and
   * omitting the filter entirely is what returns every status.
   */
  const statusFilter = selectedStatus.filter(status =>
    (RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS as string[]).includes(status),
  )

  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useListReturnRefundWorklistQuery(
    {
      merchantID,
      submittedAtFrom: toEpochMs(fromTime),
      submittedAtTo: toEpochMs(endTime, true),
      statusFilter: statusFilter.length ? statusFilter : undefined,
    },
    { skip: !merchantID },
  )

  const rows = useMemo(() => toReturnRefundRequestRows(data?.requests), [data])

  /*
   * The ORDER id — what the review-detail endpoint is keyed by — captured at
   * click time and never re-derived from the live list: a submitted decision can
   * move the request out of the current filter, and a per-render lookup that
   * dies with the row would unmount the open drawer mid-look. A row without one
   * has nothing to fetch and stays unclickable.
   */
  const [openOrderID, setOpenOrderID] = useState<string>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const {
    detail: openDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch: refetchDetail,
    submitDecisions,
    submitInspections,
    requestClarification,
    uploadPhoto,
    removePhoto,
    isMediaBusy,
  } = useReturnRequestDetail({
    merchantID,
    orderID: openOrderID,
  })

  const handleRowClick = (orderID: string) => {
    if (!orderID) {
      return
    }
    setOpenOrderID(orderID)
    setIsDrawerOpen(true)
  }

  return (
    <>
      <ReturnRefundListFilter
        storeList={filteredList}
        merchantID={merchantID}
        onChangeMerchantID={id => handleQueryChange({ merchantID: id })}
        fromTime={fromTime}
        endTime={endTime}
        onChangeDateRange={(from, end) =>
          handleQueryChange({ fromTime: from, endTime: end })
        }
        selectedStatus={selectedStatus}
        onChangeStatus={status => handleQueryChange({ status })}
        isSingleStoreUser={isSingleStoreUser}
        handleFilter={handleFilter}
        isFetchingOutletsSearch={isFetchingOutletsSearch}
      />
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="key"
        loading={isFetching}
        pagination={false}
        rowClassName={styles.clickableRow}
        onRow={(row: ReturnRefundRequestRow) => ({
          onClick: () => handleRowClick(row.orderID),
        })}
        locale={{
          emptyText: isError ? (
            /*
             * A failed fetch and a genuinely empty worklist both render zero rows, so
             * without this the merchant reads "no requests" when the truth is "not
             * loaded". The filters stay mounted above so the retry can be paired with
             * changing the store or date range.
             */
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={RETURN_REFUND_TEXT.errorState}
            >
              <Button onClick={() => refetch()}>
                {RETURN_REFUND_TEXT.errorRetry}
              </Button>
            </Empty>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={RETURN_REFUND_TEXT.emptyState}
            />
          ),
        }}
      />
      <ReturnRequestDrawer
        open={isDrawerOpen}
        detail={openDetail}
        loading={isDetailLoading}
        error={isDetailError}
        onRetry={refetchDetail}
        onClose={() => setIsDrawerOpen(false)}
        onSubmitReview={submitDecisions}
        onSubmitInspection={submitInspections}
        onRequestClarification={requestClarification}
        onUploadPhoto={uploadPhoto}
        onRemovePhoto={removePhoto}
        isMediaBusy={isMediaBusy}
        /*
         * The fallback path only — a timeline action whose destination resolves is
         * rendered as a real anchor by `ReturnStatusTimeline` and never comes here.
         * What does reach it cannot validate (an absent destination — the support
         * hand-off pending BE's deeplink TODO and the chat slice — or a deeplink the
         * helpers could not unwrap), so `openTimelineDestination` is a guard that
         * stays a silent no-op rather than a path that opens anything today.
         */
        onContextualAction={(_action, _requestID, href) =>
          openTimelineDestination(href)
        }
      />
    </>
  )
}
